import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
} from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  FILTER_ALL,
  FILTER_STARRED,
  filterResearchRuns,
  type ResearchRunFilterState,
} from '@/lib/research-run-filters';
import { api } from '@/lib/api';
import { RESEARCH_PROVIDER_LABELS } from '@/lib/research-ui-defaults';
import type { ApiResponse } from '@/types';
import type { ResearchFocusArea, ResearchRun } from '@/types';
import { RESEARCH_PRESET_QUESTIONS } from '@/data/research-preset-questions';
import { ResearchRunDetailPanel } from './ResearchRunDetailPanel';
import { ResearchQueryHelper } from './ResearchQueryHelper';
import { RunStatusIcon, truncateQuery } from './research-shared';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Filter,
  List,
  Loader2,
  PanelRight,
  Search,
  Sparkles,
  Star,
  Trash2,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const FOCUS_AREA_LABELS: Record<ResearchFocusArea, string> = {
  market: 'Marktanalyse',
  competitors: 'Wettbewerb',
  audience: 'Zielgruppe',
  channels: 'Kanäle',
  trends: 'Trends',
};

const PROVIDER_LABELS = RESEARCH_PROVIDER_LABELS;

const EMPTY_FILTERS: ResearchRunFilterState = {
  search: '',
  provider: FILTER_ALL,
  status: FILTER_ALL,
  focus: FILTER_ALL,
  star: FILTER_ALL,
};

type ProviderChoice = 'openai' | 'modelrouter' | 'azure_foundry' | 'azure_cognitive_services' | 'custom_o3gpt';

export interface ResearchWorkspaceProps {
  runs: ResearchRun[];
  scope: 'strategy' | 'standalone';
  strategyId?: string;
  strategyTitle?: string;
  strategyYear?: number;
  researchQuery: string;
  setResearchQuery: Dispatch<SetStateAction<string>>;
  focusArea: ResearchFocusArea;
  setFocusArea: (v: ResearchFocusArea) => void;
  researchProvider: ProviderChoice;
  setResearchProvider: (v: ProviderChoice) => void;
  onStartResearch: () => void | Promise<void>;
  startingResearch: boolean;
  onRefresh: () => void;
  queueNotice?: React.ReactNode;
  /** When no runs after load */
  emptyHint?: React.ReactNode;
}

export function ResearchWorkspace({
  runs,
  scope,
  strategyId,
  strategyTitle,
  strategyYear,
  researchQuery,
  setResearchQuery,
  focusArea,
  setFocusArea,
  researchProvider,
  setResearchProvider,
  onStartResearch,
  startingResearch,
  onRefresh,
  queueNotice,
  emptyHint,
}: ResearchWorkspaceProps) {
  const [filters, setFilters] = useState<ResearchRunFilterState>({ ...EMPTY_FILTERS });
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(true);
  const [mobileShowList, setMobileShowList] = useState(true);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ResearchRun | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [starBusyId, setStarBusyId] = useState<string | null>(null);

  const sortedRuns = useMemo(() => {
    const list = [...runs];
    list.sort((a, b) => {
      const sa = a.is_starred ? 1 : 0;
      const sb = b.is_starred ? 1 : 0;
      if (sa !== sb) return sb - sa;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    return list;
  }, [runs]);

  const starredCount = useMemo(() => runs.filter((r) => r.is_starred).length, [runs]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.search.trim()) n += 1;
    if (filters.provider !== FILTER_ALL) n += 1;
    if (filters.status !== FILTER_ALL) n += 1;
    if (filters.focus !== FILTER_ALL) n += 1;
    if (filters.star !== FILTER_ALL) n += 1;
    return n;
  }, [filters]);

  const filteredRuns = useMemo(() => filterResearchRuns(sortedRuns, filters), [sortedRuns, filters]);

  useEffect(() => {
    if (filteredRuns.length === 0) {
      setSelectedRunId(null);
      return;
    }
    if (!selectedRunId || !filteredRuns.some((r) => r.id === selectedRunId)) {
      const first = filteredRuns[0];
      if (first) setSelectedRunId(first.id);
    }
  }, [filteredRuns, selectedRunId]);

  const selectedRun = useMemo(
    () => filteredRuns.find((r) => r.id === selectedRunId) ?? null,
    [filteredRuns, selectedRunId]
  );

  const loadSuggestions = useCallback(async () => {
    setSuggestLoading(true);
    try {
      const existingQueries = sortedRuns.map((r) => r.query).slice(0, 40);
      let res: ApiResponse<{ questions: string[] }>;
      if (scope === 'strategy' && strategyId) {
        res = await api.post<ApiResponse<{ questions: string[] }>>(
          `/marketing-strategies/${strategyId}/research/suggest-questions`,
          { focus: focusArea, existing_queries: existingQueries }
        );
      } else {
        res = await api.post<ApiResponse<{ questions: string[] }>>('/ai/research/suggest-questions', {
          year: strategyYear ?? new Date().getFullYear(),
          title: strategyTitle ?? 'Research',
          focus: focusArea,
          existing_queries: existingQueries,
        });
      }
      if (!res.success || !res.data?.questions?.length) {
        toast.error(res.error ?? 'Keine Vorschläge erhalten.');
        return;
      }
      const first = res.data.questions[0];
      if (first) setResearchQuery(first);
      toast.success(`${res.data.questions.length} Vorschläge — erste Frage übernommen.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Vorschläge fehlgeschlagen.');
    } finally {
      setSuggestLoading(false);
    }
  }, [scope, strategyId, strategyYear, strategyTitle, focusArea, sortedRuns, setResearchQuery]);

  const applyPreset = (presetId: string) => {
    const p = RESEARCH_PRESET_QUESTIONS.find((x) => x.id === presetId);
    if (!p) return;
    setResearchQuery(p.queryTemplate);
    setFocusArea(p.focus);
  };

  const toggleStarRun = useCallback(
    async (run: ResearchRun, e?: MouseEvent) => {
      e?.stopPropagation();
      if (starBusyId) return;
      setStarBusyId(run.id);
      try {
        const next = !run.is_starred;
        let res: ApiResponse<{ is_starred?: boolean }>;
        if (scope === 'strategy' && strategyId) {
          res = await api.patch<ApiResponse<{ is_starred?: boolean }>>(
            `/marketing-strategies/${strategyId}/research/${run.id}`,
            { is_starred: next }
          );
        } else {
          res = await api.patch<ApiResponse<{ is_starred?: boolean }>>(`/ai/research-runs/${run.id}`, {
            is_starred: next,
          });
        }
        if (!res.success) {
          toast.error(res.error ?? 'Konnte Favorit nicht speichern.');
          return;
        }
        onRefresh();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Konnte Favorit nicht speichern.');
      } finally {
        setStarBusyId(null);
      }
    },
    [scope, strategyId, onRefresh, starBusyId]
  );

  const confirmDeleteRun = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      let res: ApiResponse;
      if (scope === 'strategy' && strategyId) {
        res = await api.delete<ApiResponse>(`/marketing-strategies/${strategyId}/research/${deleteTarget.id}`);
      } else {
        res = await api.delete<ApiResponse>(`/ai/research-runs/${deleteTarget.id}`);
      }
      if (!res.success) {
        toast.error(res.error ?? 'Löschen fehlgeschlagen.');
        return;
      }
      toast.success('Research-Anfrage gelöscht.');
      if (selectedRunId === deleteTarget.id) setSelectedRunId(null);
      setDeleteTarget(null);
      onRefresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget, scope, strategyId, selectedRunId, onRefresh]);

  const resetFilters = useCallback(() => setFilters({ ...EMPTY_FILTERS }), []);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {/* Kompakte Chat-Leiste: Suche + Filter-Popup */}
        <div className="flex shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
        <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
          <div className="relative min-w-0 flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-2 top-1/2 z-[1] -translate-y-1/2 text-muted"
              aria-hidden
            />
            <Input
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Fragen & Ergebnisse durchsuchen …"
              className="h-8 w-full border-border bg-background pl-8 text-xs dark:bg-card"
              aria-label="Suche in Research"
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 gap-1 border-border px-2 text-xs"
              >
                <Filter size={14} className="shrink-0 text-muted" />
                <span className="hidden sm:inline">Filter</span>
                {activeFilterCount > 0 ? (
                  <Badge
                    variant="secondary"
                    className="h-5 min-w-[1.125rem] justify-center px-1 text-[10px] tabular-nums"
                  >
                    {activeFilterCount}
                  </Badge>
                ) : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0 sm:w-80" align="end" sideOffset={6}>
              <div className="border-b border-border px-3 py-2">
                <p className="text-xs font-medium text-foreground">Liste filtern</p>
                <p className="text-[10px] text-muted">Provider, Status, Fokus, Favoriten</p>
              </div>
              <div className="max-h-[min(70vh,420px)] space-y-2 overflow-y-auto p-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted">Provider</Label>
                  <Select
                    value={filters.provider}
                    onValueChange={(v) => setFilters((f) => ({ ...f, provider: v }))}
                  >
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FILTER_ALL}>Alle</SelectItem>
                      {Object.entries(PROVIDER_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted">Status</Label>
                  <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FILTER_ALL}>Alle</SelectItem>
                      <SelectItem value="running">Läuft</SelectItem>
                      <SelectItem value="completed">Abgeschlossen</SelectItem>
                      <SelectItem value="failed">Fehler</SelectItem>
                      <SelectItem value="cancelled">Abgebrochen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted">Fokus (Präfix)</Label>
                  <Select value={filters.focus} onValueChange={(v) => setFilters((f) => ({ ...f, focus: v }))}>
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FILTER_ALL}>Alle</SelectItem>
                      {(Object.keys(FOCUS_AREA_LABELS) as ResearchFocusArea[]).map((k) => (
                        <SelectItem key={k} value={k}>
                          {FOCUS_AREA_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted">Favoriten</Label>
                  <Select value={filters.star} onValueChange={(v) => setFilters((f) => ({ ...f, star: v }))}>
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FILTER_ALL}>Alle</SelectItem>
                      <SelectItem value={FILTER_STARRED}>Nur Favoriten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {activeFilterCount > 0 ? (
                <div className="border-t border-border p-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-full text-xs text-muted"
                    onClick={resetFilters}
                  >
                    Alle Filter zurücksetzen
                  </Button>
                </div>
              ) : null}
            </PopoverContent>
          </Popover>
        </div>

        {/* Composer — Eingabebereich unter der kompakten Leiste */}
        <div className="bg-card dark:bg-background/30">
          <button
            type="button"
            onClick={() => setComposerOpen((o) => !o)}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-muted dark:text-foreground"
          >
            <span>Neue Research-Anfrage</span>
            {composerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {composerOpen && (
            <div className="space-y-3 border-t border-border px-3 pb-3 pt-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="grid min-w-[200px] flex-1 gap-1">
                  <Label className="text-xs">Vorlage</Label>
                  <Select onValueChange={(v) => applyPreset(v)}>
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Vorgefertigte Frage wählen …" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[min(60vh,320px)]">
                      {(Object.keys(FOCUS_AREA_LABELS) as ResearchFocusArea[]).map((fk) => (
                        <div key={fk}>
                          <div className="px-2 py-1.5 text-xs font-medium text-muted">
                            {FOCUS_AREA_LABELS[fk]}
                          </div>
                          {RESEARCH_PRESET_QUESTIONS.filter((q) => q.focus === fk).map((q) => (
                            <SelectItem key={q.id} value={q.id} className="text-xs">
                              {q.label}
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 gap-1 text-xs"
                  disabled={suggestLoading}
                  onClick={() => void loadSuggestions()}
                >
                  {suggestLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  KI-Vorschläge
                </Button>
                <ResearchQueryHelper
                  scope={scope}
                  strategyId={strategyId}
                  strategyTitle={strategyTitle}
                  strategyYear={strategyYear}
                  focusArea={focusArea}
                  draftQuery={researchQuery}
                  onAppendToQuery={(block) =>
                    setResearchQuery((prev) => (prev.trim() ? `${prev.trim()}\n\n${block}` : block))
                  }
                  disabled={startingResearch}
                />
              </div>
              <div className="grid min-w-0 gap-1">
                <Label className="text-xs">Forschungsfrage</Label>
                <Textarea
                  value={researchQuery}
                  onChange={(e) => setResearchQuery(e.target.value)}
                  placeholder="z. B. Welche Kanäle nutzt die Konkurrenz in der Region? (Enter = starten, Shift+Enter = neue Zeile)"
                  rows={4}
                  className="min-h-[100px] w-full resize-y text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void onStartResearch();
                    }
                  }}
                />
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="grid gap-1">
                  <Label className="text-xs">Fokus</Label>
                  <Select value={focusArea} onValueChange={(v) => setFocusArea(v as ResearchFocusArea)}>
                    <SelectTrigger className="h-9 w-36 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(FOCUS_AREA_LABELS) as ResearchFocusArea[]).map((k) => (
                        <SelectItem key={k} value={k}>
                          {FOCUS_AREA_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Provider</Label>
                  <Select
                    value={researchProvider}
                    onValueChange={(v) => setResearchProvider(v as ProviderChoice)}
                  >
                    <SelectTrigger className="h-9 w-48 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI o3</SelectItem>
                      <SelectItem value="modelrouter">Model Router</SelectItem>
                      <SelectItem value="azure_cognitive_services">Azure Cognitive Services</SelectItem>
                      <SelectItem value="azure_foundry">Azure Foundry (Bing)</SelectItem>
                      {scope === 'standalone' && <SelectItem value="custom_o3gpt">Custom O3GPT</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  onClick={() => void onStartResearch()}
                  disabled={startingResearch || !researchQuery.trim()}
                  className="h-9 gap-2"
                >
                  {startingResearch ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Start…
                    </>
                  ) : (
                    <>
                      <Search size={14} /> Research starten
                    </>
                  )}
                </Button>
              </div>
              {queueNotice}
            </div>
          )}
        </div>
        </div>

      {/* Split: list + detail */}
      {sortedRuns.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-border p-8 text-center">
          {emptyHint ?? (
            <p className="text-sm text-muted">Noch keine Research-Runs. Nutze das Formular oben.</p>
          )}
        </div>
      ) : filteredRuns.length === 0 ? (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-6 text-center text-sm text-warning">
          Keine Treffer für die aktuellen Filter.{' '}
          <button type="button" className="underline" onClick={resetFilters}>
            Filter zurücksetzen
          </button>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-2 lg:flex-row lg:gap-3">
          {/* List — hidden on small when detail selected */}
          <aside
            className={cn(
              'flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card dark:bg-card/40 lg:w-[min(100%,320px)]',
              !mobileShowList && selectedRun && 'hidden lg:flex',
              mobileShowList && 'min-h-[200px] max-h-[40vh] lg:max-h-none lg:min-h-0'
            )}
          >
            <div className="flex items-center justify-between border-b border-border px-2 py-2 lg:hidden">
              <span className="text-xs font-medium text-muted">
                Anfragen
                {starredCount > 0 ? (
                  <span className="ml-1.5 font-normal text-warning">
                    · {starredCount} Favorit{starredCount === 1 ? '' : 'en'}
                  </span>
                ) : null}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setMobileShowList(false)}
                disabled={!selectedRun}
              >
                <PanelRight size={14} className="mr-1" /> Inhalt
              </Button>
            </div>
            <div className="hidden border-b border-border px-3 py-2 text-[11px] text-muted lg:block">
              {sortedRuns.length} Anfrage{sortedRuns.length === 1 ? '' : 'n'}
              {starredCount > 0 ? (
                <span className="ml-2 text-warning">
                  · {starredCount} Favorit{starredCount === 1 ? '' : 'en'}
                </span>
              ) : null}
            </div>
            <ScrollArea className="min-h-0 flex-1">
              <ul className="space-y-0.5 p-1">
                {filteredRuns.map((r) => (
                  <li key={r.id}>
                    <div
                      className={cn(
                        'group/run flex w-full items-start gap-1 rounded-md px-1.5 py-2 text-left text-xs transition-colors',
                        selectedRunId === r.id
                          ? 'bg-card-hover ring-1 ring-border'
                          : 'hover:bg-background dark:hover:bg-card-hover/60'
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRunId(r.id);
                          setMobileShowList(false);
                        }}
                        className="flex min-w-0 flex-1 items-start gap-2 rounded-md px-1 py-0.5 text-left"
                      >
                        <div className="mt-0.5 shrink-0">
                          <RunStatusIcon status={r.status} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-3 font-medium text-foreground">
                            {truncateQuery(r.query, 100)}
                          </p>
                          <p className="mt-1 text-[11px] leading-snug text-muted">
                            {r.provider} · {new Date(r.created_at).toLocaleDateString('de-CH')}
                          </p>
                        </div>
                      </button>
                      <div className="flex shrink-0 flex-col gap-0.5 pt-0.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => void toggleStarRun(r, e)}
                              disabled={starBusyId === r.id}
                              className={cn(
                                'rounded-md p-1 transition-colors',
                                r.is_starred
                                  ? 'text-warning'
                                  : 'text-muted hover:text-warning'
                              )}
                              aria-label={r.is_starred ? 'Favorit entfernen' : 'Als Favorit'}
                            >
                              {starBusyId === r.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Star size={14} className={cn(r.is_starred && 'fill-warning text-warning')} />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="left">{r.is_starred ? 'Favorit entfernen' : 'Als Favorit'}</TooltipContent>
                        </Tooltip>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="rounded-md p-1 text-muted opacity-100 hover:bg-card-hover/80 hover:text-foreground group-hover/run:opacity-100 dark:hover:bg-card-hover dark:hover:text-foreground md:opacity-0 md:group-hover/run:opacity-100"
                              aria-label="Aktionen"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical size={14} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              className="gap-2 text-xs text-danger focus:text-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(r);
                              }}
                            >
                              <Trash2 size={14} /> Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </aside>

          {/* Detail */}
          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col',
              mobileShowList && selectedRun && 'hidden lg:flex'
            )}
          >
            <div className="mb-2 flex items-center gap-2 lg:hidden">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1 text-xs"
                onClick={() => setMobileShowList(true)}
              >
                <ChevronLeft size={14} /> Liste
              </Button>
              <span className="text-xs text-muted">
                <List size={12} className="inline mr-1" />
                {filteredRuns.length} Treffer
              </span>
            </div>
            {selectedRun ? (
              <ResearchRunDetailPanel
                run={selectedRun}
                scope={scope}
                strategyId={strategyId}
                onUpdated={onRefresh}
                onRequestDelete={(run) => setDeleteTarget(run)}
                onToggleStar={(run) => void toggleStarRun(run)}
                starBusy={starBusyId === selectedRun.id}
              />
            ) : null}
          </div>
        </div>
      )}

      <Dialog open={deleteTarget != null} onOpenChange={(o) => !o && !deleteBusy && setDeleteTarget(null)}>
        <DialogContent showClose={!deleteBusy}>
          <DialogHeader>
            <DialogTitle>Research-Anfrage löschen?</DialogTitle>
            <DialogDescription className="text-left">
              Der Eintrag und das gespeicherte Ergebnis werden unwiderruflich entfernt. Läufe in Status «Läuft» solltest
              du vorher abbrechen.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget ? (
            <p className="max-h-32 overflow-y-auto rounded-md border border-border bg-background p-2 text-xs text-muted dark:bg-card">
              {truncateQuery(deleteTarget.query, 280)}
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" disabled={deleteBusy} onClick={() => setDeleteTarget(null)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteBusy}
              className="gap-1.5"
              onClick={() => void confirmDeleteRun()}
            >
              {deleteBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </TooltipProvider>
  );
}
